import * as Sentry from '@sentry/browser';
import { ErrorLink } from '@apollo/client/link/error';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import type { Operation } from '@apollo/client/core';
import type { GraphQLFormattedError } from 'graphql';

type ReportInput = {
  networkError: Error | undefined;
  graphQLErrors: readonly GraphQLFormattedError[] | undefined;
  operation: Operation;
};

export const reportApolloError = ({
  networkError,
  graphQLErrors,
  operation
}: ReportInput): void => {
  const operationName = operation.operationName ?? 'unknown';

  const variableKeys = Object.keys(operation.variables ?? {});

  if (networkError && networkError.name !== 'AbortError') {
    Sentry.withScope((scope) => {
      scope.setTag('graphql.operation', operationName);
      scope.setTag('graphql.kind', 'networkError');
      scope.setContext('graphql', { variableKeys });
      Sentry.captureException(networkError);
    });
  }

  if (graphQLErrors?.length) {
    for (const err of graphQLErrors) {
      Sentry.withScope((scope) => {
        scope.setTag('graphql.operation', operationName);
        scope.setTag('graphql.kind', 'graphQLError');
        scope.setLevel('warning');
        scope.setFingerprint(['graphql', operationName, err.message]);
        scope.setContext('graphql', {
          path: err.path,
          variableKeys
        });
        Sentry.captureMessage(`GraphQL error: ${err.message}`);
      });
    }
  }
};

export const sentryErrorLink: ErrorLink = new ErrorLink(
  ({ error, operation }) => {
    if (CombinedGraphQLErrors.is(error)) {
      reportApolloError({
        networkError: undefined,
        graphQLErrors: error.errors,
        operation
      });
    } else {
      reportApolloError({
        networkError: error instanceof Error ? error : new Error(String(error)),
        graphQLErrors: undefined,
        operation
      });
    }
  }
);
